/**
 * Browser platform for the Flash-compatibility runtime.
 *
 * - now(): performance.now() (wall clock; getTimer() and SOUND_COMPLETE follow real time);
 * - random(): seeded mulberry32 (seed option; default seed from crypto.getRandomValues);
 * - storage: localStorage with a key prefix (memory fallback when localStorage is unavailable,
 *   e.g. blocked cookies / private mode quota);
 * - readText/readBinary: fetch, resolved against `baseUrl` (default: document.baseURI);
 * - decodeImage: exact PNG decoder (browser-png.ts) -> straight RGBA identical to the headless
 *   NodePlatform; other formats via createImageBitmap + canvas getImageData (straight alpha,
 *   lossy for semi-transparent pixels because canvases are premultiplied);
 * - measureText: canvas 2D measureText with the same CSS font the renderer draws with
 *   (FontResolver), cached; fontMetrics: Flash-font table, else host font bounding box;
 * - audio: WebAudioBackend (browser-audio.ts);
 * - scheduleFrame: requestAnimationFrame.
 *
 * DOM access happens lazily (constructing the platform in Node works; tests inject fakes).
 * No VK/network services: files come only from the game's own static asset folder.
 */
import type { AudioBackend, DecodedImage, FontMetrics, FontSpec, Platform, PlatformStorage } from './types';
import { decodePng, isPng } from './browser-png';
import { EmbeddedFont, FontResolver, loadFontFaces } from './browser-fonts';
import { WebAudioBackend } from './browser-audio';

export { FontResolver, loadFontFaces } from './browser-fonts';
export type { EmbeddedFont } from './browser-fonts';
export { WebAudioBackend, planPlayback } from './browser-audio';
export type { AudioContextLike, AudioPlayRequestEx, WebAudioBackendOptions } from './browser-audio';
export { decodePng, isPng, inflateZlib } from './browser-png';

/** Deterministic 32-bit PRNG (mulberry32; same algorithm as NodePlatform). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** In-memory storage (fallback / tests). */
export class MemoryStorage implements PlatformStorage {
  readonly map = new Map<string, string>();
  get(key: string): string | null {
    return this.map.has(key) ? (this.map.get(key) as string) : null;
  }
  set(key: string, value: string): void {
    this.map.set(key, String(value));
  }
  remove(key: string): void {
    this.map.delete(key);
  }
}

/** Minimal Web Storage interface (window.localStorage or a fake). */
export interface WebStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * localStorage-backed storage with a key prefix. Every access is guarded: when the browser refuses
 * (SecurityError, QuotaExceededError) the value is kept in memory for this session and a warning
 * is logged once.
 */
export class LocalStorageStorage implements PlatformStorage {
  private readonly memory = new MemoryStorage();
  private warned = false;
  constructor(
    private readonly ls: WebStorageLike | null,
    readonly prefix = 'fwp:',
  ) {}
  private fail(err: unknown): void {
    if (this.warned) return;
    this.warned = true;
    // eslint-disable-next-line no-console
    console.warn('[flash] localStorage unavailable, falling back to memory storage', err);
  }
  get(key: string): string | null {
    const mem = this.memory.get(key);
    if (mem !== null) return mem;
    if (!this.ls) return null;
    try {
      return this.ls.getItem(this.prefix + key);
    } catch (err) {
      this.fail(err);
      return null;
    }
  }
  set(key: string, value: string): void {
    if (this.ls) {
      try {
        this.ls.setItem(this.prefix + key, String(value));
        this.memory.remove(key);
        return;
      } catch (err) {
        this.fail(err);
      }
    }
    this.memory.set(key, value);
  }
  remove(key: string): void {
    this.memory.remove(key);
    if (!this.ls) return;
    try {
      this.ls.removeItem(this.prefix + key);
    } catch (err) {
      this.fail(err);
    }
  }
}

function defaultLocalStorage(): WebStorageLike | null {
  try {
    const ls = (globalThis as any).localStorage;
    return ls && typeof ls.getItem === 'function' ? ls : null;
  } catch {
    return null;
  }
}

function randomSeed(): number {
  const c: any = (globalThis as any).crypto;
  if (c && typeof c.getRandomValues === 'function') {
    const a = new Uint32Array(1);
    c.getRandomValues(a);
    return a[0];
  }
  return (performance.now() * 1000) >>> 0;
}

/** A 2D context able to measure text (CanvasRenderingContext2D, OffscreenCanvas 2D or a fake). */
export interface MeasureContext {
  font: string;
  measureText(text: string): { width: number; fontBoundingBoxAscent?: number; fontBoundingBoxDescent?: number };
}

function defaultMeasureContext(): MeasureContext | null {
  const g: any = globalThis as any;
  try {
    if (typeof g.OffscreenCanvas === 'function') {
      const ctx = new g.OffscreenCanvas(1, 1).getContext('2d');
      if (ctx) return ctx;
    }
    if (g.document) {
      const ctx = g.document.createElement('canvas').getContext('2d');
      if (ctx) return ctx;
    }
  } catch {
    /* no canvas */
  }
  return null;
}

export interface BrowserPlatformOptions {
  /** Base URL for relative asset URLs (default: document.baseURI or ''). */
  baseUrl?: string;
  /** RNG seed for `random()` (default: random). */
  seed?: number;
  /** Key prefix for localStorage (default 'fwp:'). */
  storagePrefix?: string;
  /** Storage override (default: localStorage with memory fallback). */
  storage?: PlatformStorage;
  /** Audio backend; `false` disables sound (default: WebAudioBackend). */
  audio?: AudioBackend | false;
  /** Canvas 2D context used for text measuring (default: OffscreenCanvas / <canvas>). */
  measureContext?: MeasureContext | null;
  /** Font resolver shared with the renderer (default: new FontResolver()). */
  fonts?: FontResolver;
  /** fetch implementation (default: globalThis.fetch). */
  fetch?: (url: string, init?: any) => Promise<any>;
  /** Clock override (tests). */
  now?: () => number;
  /** Max cached text measurements (default 20000). */
  measureCacheSize?: number;
  /**
   * URLs decoded by the browser's native decoder off the main thread (createImageBitmap) instead of the exact
   * JS PNG decoder. Default: shape atlas pages (`.../atlas/N.png`) and HD variants (`.../bitmaps-hd/N.png`), which
   * are render-only; BitmapData sources
   * (maps, crater masks, bitmaps) always use the exact decoder because game logic reads their pixels.
   */
  nativeDecode?: (url: string) => boolean;
}

export class BrowserPlatform implements Platform {
  readonly kind = 'browser' as const;
  readonly baseUrl: string;
  storage: PlatformStorage;
  audio: AudioBackend | undefined;
  /** Font resolver: register embedded fonts with `loadFonts()`; pass to the renderer. */
  readonly fonts: FontResolver;
  private rng: () => number;
  private seedValue: number;
  private readonly clock: () => number;
  private readonly fetchImpl: (url: string, init?: any) => Promise<any>;
  private measureCtx: MeasureContext | null | undefined;
  private lastFont = '';
  private readonly widthCache = new Map<string, number>();
  private readonly metricsCache = new Map<string, FontMetrics>();
  private readonly measureCacheSize: number;
  private readonly nativeDecode: (url: string) => boolean;
  private fontsVersion = -1;

  constructor(opts: BrowserPlatformOptions = {}) {
    const g: any = globalThis as any;
    this.baseUrl = opts.baseUrl ?? (g.document?.baseURI ? String(g.document.baseURI) : '');
    this.seedValue = (opts.seed ?? randomSeed()) >>> 0;
    this.rng = mulberry32(this.seedValue);
    this.storage = opts.storage ?? new LocalStorageStorage(defaultLocalStorage(), opts.storagePrefix ?? 'fwp:');
    this.clock = opts.now ?? (() => performance.now());
    this.audio =
      opts.audio === false
        ? undefined
        : (opts.audio ??
          new WebAudioBackend({
            now: this.clock,
            // Same URL resolution / fetch as every other asset.
            load: async (url: string) => {
              const b = await this.readBinary(url);
              return b.buffer.slice(b.byteOffset, b.byteOffset + b.byteLength) as ArrayBuffer;
            },
          }));
    this.fonts = opts.fonts ?? new FontResolver();
    this.fetchImpl = opts.fetch ?? ((url: string, init?: any) => g.fetch(url, init));
    this.measureCtx = opts.measureContext === undefined ? undefined : opts.measureContext;
    this.measureCacheSize = opts.measureCacheSize ?? 20000;
    this.nativeDecode = opts.nativeDecode ?? ((url: string) => /(^|\/)(atlas|bitmaps-hd)\/[^/]+\.png$/.test(url));
  }

  now(): number {
    return this.clock();
  }

  random(): number {
    return this.rng();
  }

  setSeed(seed: number): void {
    this.seedValue = seed >>> 0;
    this.rng = mulberry32(this.seedValue);
  }

  get seed(): number {
    return this.seedValue;
  }

  /** Resolve a (relative) URL against the base URL. */
  resolveUrl(url: string): string {
    if (!this.baseUrl || /^[a-z][a-z0-9+.-]*:/i.test(url)) return url;
    try {
      return new URL(url, this.baseUrl).href;
    } catch {
      return url;
    }
  }

  private async fetchOk(url: string): Promise<any> {
    const res = await this.fetchImpl(this.resolveUrl(url));
    if (!res || !res.ok) throw new Error(`Failed to load ${url}: HTTP ${res ? res.status : '?'}`);
    return res;
  }

  async readText(url: string): Promise<string> {
    return (await this.fetchOk(url)).text();
  }

  async readBinary(url: string): Promise<Uint8Array> {
    return new Uint8Array(await (await this.fetchOk(url)).arrayBuffer());
  }

  async decodeImage(bytes: Uint8Array, url?: string, opts: { exact?: boolean } = {}): Promise<DecodedImage> {
    // Measured: exact JS decoding (inflate + unfilter) of 2048x2048 atlas pages plus putImageData uploads ran on
    // the main thread and produced 100-270 ms frames whenever a new screen/battle first showed its shapes.
    if (!opts.exact && url && this.nativeDecode(url) && typeof (globalThis as any).createImageBitmap === 'function') {
      try {
        return await decodeForRender(bytes);
      } catch {
        // Fall back to the exact decoder below.
      }
    }
    if (isPng(bytes)) {
      try {
        return await decodePng(bytes);
      } catch (err) {
        // Fall through to the browser decoder (e.g. no DecompressionStream).
        if (typeof (globalThis as any).createImageBitmap !== 'function') throw err;
      }
    }
    return decodeWithImageBitmap(bytes);
  }

  // ------------------------------------------------------------------ text
  private ctx(): MeasureContext | null {
    if (this.measureCtx === undefined) this.measureCtx = defaultMeasureContext();
    return this.measureCtx;
  }

  private checkFonts(): void {
    if (this.fontsVersion !== this.fonts.version) {
      this.fontsVersion = this.fonts.version;
      this.widthCache.clear();
      this.metricsCache.clear();
      this.lastFont = '';
    }
  }

  /**
   * Width of `text` (canvas measureText), cached by font and text. `noCache` (the text of a password field): measured
   * without the cache, so a password - or a prefix of it, measured for the caret - never stays in page memory.
   */
  measureText(text: string, font: FontSpec, noCache = false): number {
    if (!text) return 0;
    this.checkFonts();
    const css = this.fonts.cssFont(font);
    const key = noCache ? '' : css + '\u0000' + text;
    const hit = noCache ? undefined : this.widthCache.get(key);
    if (hit !== undefined) return hit;
    const ctx = this.ctx();
    let w: number;
    if (!ctx) w = text.length * font.size * 0.55;
    else {
      if (this.lastFont !== css) {
        ctx.font = css;
        this.lastFont = css;
      }
      w = ctx.measureText(text).width;
    }
    if (noCache) return w;
    if (this.widthCache.size >= this.measureCacheSize) this.widthCache.clear();
    this.widthCache.set(key, w);
    return w;
  }

  fontMetrics(font: FontSpec): FontMetrics {
    const known = this.fonts.knownMetrics(font);
    if (known) return known;
    this.checkFonts();
    const css = this.fonts.cssFont(font);
    const hit = this.metricsCache.get(css);
    if (hit) return hit;
    let m: FontMetrics = { ascent: font.size * 0.9, descent: font.size * 0.25 };
    const ctx = this.ctx();
    if (ctx) {
      ctx.font = css;
      this.lastFont = css;
      const tm = ctx.measureText('Mg');
      if (tm.fontBoundingBoxAscent !== undefined && tm.fontBoundingBoxDescent !== undefined && tm.fontBoundingBoxAscent > 0) {
        m = { ascent: tm.fontBoundingBoxAscent, descent: tm.fontBoundingBoxDescent };
      }
    }
    this.metricsCache.set(css, m);
    return m;
  }

  /**
   * Register the library's embedded fonts (AssetLibrary.fonts) with the FontFace API and the
   * shared FontResolver. Call before the game creates text fields (layouts measured earlier would
   * use fallback fonts). Returns the number of loaded font files.
   */
  async loadFonts(fonts: readonly EmbeddedFont[]): Promise<number> {
    const withUrl = fonts.filter((f) => !!f.url).map((f) => ({ ...f, url: this.resolveUrl(f.url as string) }));
    const n = await loadFontFaces(withUrl);
    this.fonts.register(withUrl);
    return n;
  }

  /**
   * flash.system.System.setClipboard: async Clipboard API (works inside the user gesture that
   * triggered it, which is the case for game buttons since input is dispatched synchronously),
   * falling back to a temporary <textarea> + execCommand('copy').
   */
  setClipboard(text: string): void {
    const g: any = globalThis as any;
    const fallback = () => {
      const doc = g.document;
      if (!doc || !doc.body) return;
      const ta = doc.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      doc.body.appendChild(ta);
      ta.select();
      try {
        doc.execCommand('copy');
      } catch {
        /* unsupported */
      }
      doc.body.removeChild(ta);
    };
    const clip = g.navigator?.clipboard;
    if (clip && typeof clip.writeText === 'function') clip.writeText(text).catch(fallback);
    else fallback();
  }

  scheduleFrame(cb: (now: number) => void): () => void {
    const g: any = globalThis as any;
    if (typeof g.requestAnimationFrame === 'function') {
      const id = g.requestAnimationFrame(cb);
      return () => g.cancelAnimationFrame(id);
    }
    const id = setTimeout(() => cb(this.clock()), 16);
    return () => clearTimeout(id);
  }
}

/** createImageBitmap + canvas readback (straight alpha, via the browser's premultiplied canvas). */
/**
 * Render-only decode: the browser decodes off the main thread and the ImageBitmap is drawn directly (no
 * putImageData upload). `data` (straight RGBA, needed only for shape hit-testing, colour-transform tints and
 * BitmapData.draw) is read back lazily on first access. UNVERIFIED parity: the canvas round trip may round RGB
 * of semi-transparent pixels (alpha is exact); this only affects tinted edges, never game logic.
 */
export async function decodeForRender(bytes: Uint8Array): Promise<DecodedImage> {
  const g: any = globalThis as any;
  const bmp = await g.createImageBitmap(new Blob([bytes as unknown as BlobPart], { type: 'image/png' }), { premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
  prewarm(bmp);
  const img: any = { width: bmp.width, height: bmp.height, source: bmp };
  let pixels: Uint8ClampedArray | null = null;
  Object.defineProperty(img, 'data', {
    enumerable: true,
    get(): Uint8ClampedArray {
      if (!pixels) pixels = readPixels(bmp);
      return pixels;
    },
  });
  return img as DecodedImage;
}

let warmCtx: any = undefined;
/**
 * Draws a freshly decoded ImageBitmap once into a tiny scratch canvas right after decoding (outside the frame loop),
 * so the rasterizer's one-time preparation of the image (texture upload) does not land in the first game frame that
 * shows it (measured: 33-50 ms frames when many new sprites appeared at once).
 */
function prewarm(bmp: any): void {
  const g: any = globalThis as any;
  if (warmCtx === undefined) {
    warmCtx = null;
    try {
      if (typeof g.OffscreenCanvas === 'function') warmCtx = new g.OffscreenCanvas(1, 1).getContext('2d');
      else if (g.document) {
        const c = g.document.createElement('canvas');
        c.width = c.height = 1;
        warmCtx = c.getContext('2d');
      }
    } catch {
      warmCtx = null;
    }
  }
  if (!warmCtx) return;
  try {
    warmCtx.drawImage(bmp, 0, 0, 1, 1);
  } catch {
    /* ignore */
  }
}

function readPixels(bmp: any): Uint8ClampedArray {
  const g: any = globalThis as any;
  const w = bmp.width;
  const h = bmp.height;
  let ctx: any = null;
  if (typeof g.OffscreenCanvas === 'function') ctx = new g.OffscreenCanvas(w, h).getContext('2d', { willReadFrequently: true });
  else if (g.document) {
    const c = g.document.createElement('canvas');
    c.width = w;
    c.height = h;
    ctx = c.getContext('2d', { willReadFrequently: true });
  }
  if (!ctx) throw new Error('No 2D canvas available to read image pixels');
  ctx.drawImage(bmp, 0, 0);
  return ctx.getImageData(0, 0, w, h).data;
}

export async function decodeWithImageBitmap(bytes: Uint8Array): Promise<DecodedImage> {
  const g: any = globalThis as any;
  if (typeof g.createImageBitmap !== 'function') throw new Error('createImageBitmap is not available');
  const bmp = await g.createImageBitmap(new Blob([bytes as unknown as BlobPart]), { premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
  const w = bmp.width;
  const h = bmp.height;
  let ctx: any = null;
  if (typeof g.OffscreenCanvas === 'function') ctx = new g.OffscreenCanvas(w, h).getContext('2d', { willReadFrequently: true });
  else if (g.document) {
    const c = g.document.createElement('canvas');
    c.width = w;
    c.height = h;
    ctx = c.getContext('2d', { willReadFrequently: true });
  }
  if (!ctx) throw new Error('No 2D canvas available to decode images');
  ctx.drawImage(bmp, 0, 0);
  const id = ctx.getImageData(0, 0, w, h);
  return { width: w, height: h, data: id.data, source: bmp };
}
